// ===== Packages =====

import { ReactSVG }                                 from 'react-svg';
import styled                                       from 'styled-components';

// ===== Constants =====

import {
    BODY_FONT_SIZE,
    READER_VIEW_PERFECT_FOURTH_FONT_MULTIPLIER,
}                                                   from '../../constants/generalConstants';
import FONT_TYPE                                    from '../../constants/fontType';

// ===== Styles =====

import { theme as themeObj }                        from '../../themes/theme-context';

export const HistoryModalContainer = styled.div<{ $compact?: boolean }>`
    display: flex;
    flex-direction: column;
    width: ${({ $compact }) => ($compact ? 'min(92vw, 64rem)' : 'min(96vw, 86rem)')};
    height: ${({ $compact }) => ($compact ? 'auto' : 'min(90vh, 56rem)')};
    max-height: ${({ $compact }) => ($compact ? 'min(92vh, 46rem)' : 'min(90vh, 56rem)')};
    background: ${themeObj.color.cream500};
    border: 1px solid ${themeObj.color.neutral300};
    border-radius: ${1.25 * BODY_FONT_SIZE}px;
    box-shadow: ${themeObj.color.boxShadow400};
    overflow: hidden;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
`;

export const HistoryModalHeader = styled.div`
    position: relative;
    padding: ${1.5 * BODY_FONT_SIZE}px ${1.75 * BODY_FONT_SIZE}px ${BODY_FONT_SIZE}px;
    background: ${themeObj.color.cream500};
    border-bottom: 1px solid ${themeObj.color.neutral300};
`;

export const HistoryModalCloseButtonContainer = styled.div`
    position: absolute;
    top: ${BODY_FONT_SIZE}px;
    right: ${BODY_FONT_SIZE}px;
    background: ${themeObj.color.cream500};
    border-radius: ${999 * BODY_FONT_SIZE}px;
`;

export const HistoryModalTitle = styled.div`
    font-size: ${1.25 * BODY_FONT_SIZE}px;
    font-weight: 700;
    color: ${themeObj.color.textBlack};
`;

export const HistoryModalSubtitle = styled.div`
    margin-top: ${0.375 * BODY_FONT_SIZE}px;
    font-size: ${(1.25 / READER_VIEW_PERFECT_FOURTH_FONT_MULTIPLIER) * BODY_FONT_SIZE}px;
    color: ${themeObj.color.neutral800};
    line-height: 1.5em;
`;

export const HistoryModalBody = styled.div`
    display: grid;
    grid-template-columns: minmax(17rem, 22rem) minmax(0, 1fr);
    flex: 1;
    min-height: 0;
    background: ${themeObj.color.cream500};

    @media (max-width: 1024px) {
        grid-template-columns: 1fr;
    }
`;

export const HistorySinglePaneBody = styled.div`
    display: flex;
    flex: 1;
    min-height: 0;
    background: ${themeObj.color.cream500};
    align-items: stretch;
`;

export const HistorySidebar = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 0;
    background: ${themeObj.color.cream500};
    border-right: 1px solid ${themeObj.color.neutral300};
    overflow: hidden;

    @media (max-width: 1024px) {
        border-right: none;
        border-bottom: 1px solid ${themeObj.color.neutral300};
        max-height: 16rem;
    }
`;

export const HistorySidebarControls = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${0.5 * BODY_FONT_SIZE}px;
    padding: ${0.625 * BODY_FONT_SIZE}px ${BODY_FONT_SIZE}px;
    background: ${themeObj.color.cream500};
    border-bottom: 1px solid ${themeObj.color.neutral300};
`;

export const HistorySidebarList = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
`;

export const HistorySidebarPreviewPane = styled.div`
    display: flex;
    flex: 1;
    min-height: 0;
    flex-direction: column;
    background: ${themeObj.color.white};
    border-top: 1px solid ${themeObj.color.neutral300};
`;

export const HistoryListItem = styled.button<{ selected: boolean }>`
    width: 100%;
    padding: ${BODY_FONT_SIZE}px ${1.25 * BODY_FONT_SIZE}px;
    font-family: inherit;
    color: ${themeObj.color.textBlack};
    text-align: left;
    border: none;
    border-bottom: 1px solid ${themeObj.color.neutral300};
    background: ${({ selected }) => (selected ? themeObj.color.cream400 : themeObj.color.cream500)};
    cursor: pointer;
    transition: background 120ms ease-in, color 120ms ease-in;

    &:hover {
        background: ${({ selected }) => (selected ? themeObj.color.cream400 : themeObj.color.white)};
    }
`;

export const HistoryTimestamp = styled.div`
    color: ${themeObj.color.neutral800};
    font-size: ${0.875 * BODY_FONT_SIZE}px;
    font-weight: 600;
`;

export const HistoryMetaRow = styled.div`
    display: flex;
    align-items: center;
    gap: ${0.5 * BODY_FONT_SIZE}px;
    margin-top: ${0.375 * BODY_FONT_SIZE}px;
    flex-wrap: wrap;
`;

export const HistoryVersionLabel = styled.div`
    color: ${themeObj.color.textBlack};
    font-size: ${BODY_FONT_SIZE}px;
    font-weight: 700;
`;

export const HistoryTag = styled.div<{ published?: boolean }>`
    display: inline-flex;
    align-items: center;
    padding: ${0.15 * BODY_FONT_SIZE}px ${0.45 * BODY_FONT_SIZE}px;
    border-radius: ${999 * BODY_FONT_SIZE}px;
    font-size: ${0.7 * BODY_FONT_SIZE}px;
    font-weight: 700;
    letter-spacing: 0.03em;
    color: ${themeObj.color.white};
    background: ${themeObj.verascopeColor.purple400};
`;

export const HistoryPreviewPane = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 0;
    background: ${themeObj.color.cream500};
`;

export const HistoryPreviewActions = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${0.5 * BODY_FONT_SIZE}px;
    padding: ${0.625 * BODY_FONT_SIZE}px ${1.25 * BODY_FONT_SIZE}px;
    background: ${themeObj.color.cream500};
    border-bottom: 1px solid ${themeObj.color.neutral300};
`;

export const HistoryPreviewFooter = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${0.5 * BODY_FONT_SIZE}px;
    padding: ${0.75 * BODY_FONT_SIZE}px ${1.25 * BODY_FONT_SIZE}px;
    background: ${themeObj.color.cream500};
    border-top: 1px solid ${themeObj.color.neutral300};
`;

export const HistoryPublishSummaryCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${0.625 * BODY_FONT_SIZE}px;
    padding: ${BODY_FONT_SIZE}px;
    background: ${themeObj.color.white};
    border: 1px solid ${themeObj.color.neutral300};
    border-radius: ${BODY_FONT_SIZE}px;
`;

export const HistoryPublishSummaryTitle = styled.div`
    color: ${themeObj.color.textBlack};
    font-size: ${0.95 * BODY_FONT_SIZE}px;
    font-weight: 700;
`;

export const HistoryPublishSummarySubtitle = styled.div`
    color: ${themeObj.color.neutral800};
    font-size: ${0.85 * BODY_FONT_SIZE}px;
    line-height: 1.5em;
`;

export const HistoryPublishSummaryCounts = styled.div`
    display: grid;
    gap: ${0.375 * BODY_FONT_SIZE}px;
`;

export const HistoryPublishSummaryCountRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${BODY_FONT_SIZE}px;
`;

export const HistoryPublishSummaryCountLabel = styled.div`
    color: ${themeObj.color.neutral800};
    font-size: ${0.85 * BODY_FONT_SIZE}px;
    font-weight: 600;
`;

export const HistoryPublishSummaryCountValue = styled.div`
    color: ${themeObj.color.textBlack};
    font-size: ${0.9 * BODY_FONT_SIZE}px;
    font-weight: 700;
`;

export const HistoryPublishSummaryNote = styled.div`
    color: ${themeObj.color.neutral800};
    font-size: ${0.8 * BODY_FONT_SIZE}px;
    line-height: 1.5em;
`;

export const HistoryPublishSummaryActions = styled.div`
    display: flex;
    gap: ${0.5 * BODY_FONT_SIZE}px;
    flex-wrap: wrap;
`;

export const HistoryPublishSummaryFailedList = styled.div`
    display: grid;
    gap: ${0.35 * BODY_FONT_SIZE}px;
    padding: ${0.75 * BODY_FONT_SIZE}px;
    background: ${themeObj.color.cream500};
    border: 1px solid ${themeObj.color.neutral300};
    border-radius: ${0.75 * BODY_FONT_SIZE}px;
`;

export const HistoryPublishSummaryFailedItem = styled.div`
    color: ${themeObj.color.neutral900};
    font-size: ${0.78 * BODY_FONT_SIZE}px;
    line-height: 1.45em;
    word-break: break-word;
`;

export const HistoryScopeRow = styled.div`
    display: flex;
    align-items: center;
    gap: ${0.5 * BODY_FONT_SIZE}px;
    flex-wrap: wrap;
`;

export const HistoryScopePathRow = styled.div`
    display: flex;
    align-items: center;
    gap: ${0.35 * BODY_FONT_SIZE}px;
    flex-wrap: nowrap;
    min-width: 0;
`;

export const HistoryScopeLabel = styled.div`
    color: ${themeObj.color.textBlack};
    font-size: ${0.85 * BODY_FONT_SIZE}px;
    font-weight: 600;
    white-space: nowrap;
`;

export const HistoryScopeSeparator = styled.div`
    color: ${themeObj.color.neutral700};
    font-size: ${1.1 * BODY_FONT_SIZE}px;
    line-height: 1;
    white-space: nowrap;
`;

export const HistoryScopeSelect = styled.select<{
    $compact?: boolean;
    $sidebar?: boolean;
    $fill?: boolean;
    customCursorActive?: boolean;
}>`
    min-width: ${({ $compact, $sidebar, $fill }) => {
        if ($fill) {
            return '0';
        }
        if ($compact && $sidebar) {
            return '0';
        }
        if ($compact) {
            return '9.5rem';
        }
        return '12rem';
    }};
    max-width: ${({ $compact, $sidebar, $fill }) => {
        if ($fill) {
            return 'none';
        }
        if ($compact && $sidebar) {
            return 'none';
        }
        if ($compact) {
            return '17rem';
        }
        return 'min(32rem, 100%)';
    }};
    flex: ${({ $compact, $sidebar, $fill }) => {
        if ($fill) {
            return '1 1 0';
        }
        if ($compact && $sidebar) {
            return '1 1 0';
        }
        if ($compact) {
            return '0 1 15rem';
        }
        return '1 1 18rem';
    }};
    height: ${2.05 * BODY_FONT_SIZE}px;
    padding: 0 ${2 * BODY_FONT_SIZE}px 0 ${0.625 * BODY_FONT_SIZE}px;
    border: 1px solid ${themeObj.color.neutral400};
    border-radius: ${1.025 * BODY_FONT_SIZE}px;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background-color: ${themeObj.color.white};
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='9' viewBox='0 0 14 9' fill='none'%3E%3Cpath d='M1.25 1.5L7 7.25L12.75 1.5' stroke='%23343A40' stroke-width='1.75' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right ${0.8 * BODY_FONT_SIZE}px center;
    background-size: ${0.85 * BODY_FONT_SIZE}px auto;
    color: ${themeObj.color.textBlack};
    font-size: ${0.85 * BODY_FONT_SIZE}px;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    text-overflow: ellipsis;
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'pointer')};

    &:focus {
        outline: none;
        border-color: ${themeObj.verascopeColor.purple400};
        box-shadow: 0 0 0 2px ${themeObj.color.neutral200};
    }
`;

export const HistoryActionRow = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: wrap;
    gap: ${0.4 * BODY_FONT_SIZE}px;
`;

export const HistoryTargetRow = styled.div<{ $separated?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${0.5 * BODY_FONT_SIZE}px;
    width: min(100%, 42rem);
    margin: 0 auto;
    padding-top: ${({ $separated }) => ($separated ? `${0.625 * BODY_FONT_SIZE}px` : '0')};
    border-top: ${({ $separated }) => ($separated ? `1px solid ${themeObj.color.neutral300}` : 'none')};
`;

export const HistoryTargetControls = styled.div<{
    $centered?: boolean;
}>`
    display: flex;
    align-items: center;
    justify-content: ${({ $centered }) => ($centered ? 'center' : 'space-between')};
    gap: ${0.6 * BODY_FONT_SIZE}px;
    width: ${({ $centered }) => ($centered ? 'fit-content' : '100%')};
    max-width: 100%;
    min-width: 0;
    flex-wrap: nowrap;

    @media (max-width: 900px) {
        justify-content: center;
        flex-wrap: wrap;
    }
`;

export const HistoryTargetButtonGroup = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${0.5 * BODY_FONT_SIZE}px;
    flex-wrap: nowrap;
    flex: 0 0 auto;
`;

export const HistoryTargetSelectGroup = styled.div<{
    $centered?: boolean;
}>`
    display: flex;
    align-items: center;
    justify-content: ${({ $centered }) => ($centered ? 'center' : 'flex-start')};
    gap: ${0.35 * BODY_FONT_SIZE}px;
    flex-wrap: nowrap;
    min-width: 0;
    flex: ${({ $centered }) => ($centered ? '0 1 auto' : '1 1 0')};

    @media (max-width: 900px) {
        justify-content: center;
        flex-wrap: wrap;
        min-width: min(100%, 16rem);
        flex: 1 1 16rem;
    }
`;

export const HistoryAccessPane = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    padding: ${0.85 * BODY_FONT_SIZE}px ${BODY_FONT_SIZE}px ${1.15 * BODY_FONT_SIZE}px;
    background: ${themeObj.color.cream500};
    overflow-y: auto;
    align-items: center;
`;

export const HistoryAccessSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${0.625 * BODY_FONT_SIZE}px;
    padding: ${0.75 * BODY_FONT_SIZE}px 0;
    width: min(100%, 46rem);

    &:not(:first-child) {
        border-top: 1px solid ${themeObj.color.neutral300};
    }
`;

export const HistoryAccessSectionHeader = styled.div`
    color: ${themeObj.color.textBlack};
    font-size: ${0.95 * BODY_FONT_SIZE}px;
    font-weight: 700;
`;

export const HistoryAccessGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: ${0.85 * BODY_FONT_SIZE}px;
    width: min(100%, 42rem);
`;

export const HistoryAccessField = styled.div<{ $spanFull?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${0.45 * BODY_FONT_SIZE}px;
    min-width: 0;
    grid-column: ${({ $spanFull }) => ($spanFull ? '1 / -1' : 'auto')};
    width: 100%;
    padding: ${0.8 * BODY_FONT_SIZE}px;
    border: 1px solid ${themeObj.color.neutral300};
    border-radius: ${1.05 * BODY_FONT_SIZE}px;
    background: ${themeObj.color.white};
`;

export const HistoryAccessRow = styled.div`
    display: flex;
    align-items: center;
    gap: ${0.75 * BODY_FONT_SIZE}px;
    flex-wrap: nowrap;
    padding-top: ${0.625 * BODY_FONT_SIZE}px;
    border-top: 1px solid ${themeObj.color.neutral300};
    overflow-x: auto;

    @media (max-width: 1280px) {
        flex-wrap: wrap;
    }
`;

export const HistoryAccessGroup = styled.div`
    display: flex;
    align-items: center;
    gap: ${0.45 * BODY_FONT_SIZE}px;
    min-width: 0;
    flex: 0 0 auto;
`;

export const HistoryAccessLabel = styled.div`
    color: ${themeObj.color.textBlack};
    font-size: ${0.85 * BODY_FONT_SIZE}px;
    font-weight: 600;
    white-space: nowrap;
    text-align: center;
`;

export const HistoryAccessLabelRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${0.35 * BODY_FONT_SIZE}px;
    background: ${themeObj.color.white};
    border-radius: ${0.8 * BODY_FONT_SIZE}px;
`;

export const HistoryAccessInfoButton = styled.button<{ customCursorActive: boolean }>`
    width: ${1.1 * BODY_FONT_SIZE}px;
    height: ${1.1 * BODY_FONT_SIZE}px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: ${themeObj.color.white};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'default' : 'pointer')};

    &:hover,
    &:focus-visible {
        background: ${themeObj.color.white};
        outline: none;
    }
`;

export const HistoryAccessInfoIcon = styled(ReactSVG)`
    display: inline-flex;
    width: ${0.72 * BODY_FONT_SIZE}px;
    height: ${0.72 * BODY_FONT_SIZE}px;

    & > div {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    & svg {
        width: 100%;
        height: 100%;
        display: block;
        transform: scale(1.15);
        transform-origin: center;
    }

    & path,
    & circle {
        fill: ${themeObj.color.neutral800};
        stroke: ${themeObj.color.neutral800};
        transition: fill 120ms ease, stroke 120ms ease;
    }

    ${HistoryAccessInfoButton}:hover & path,
    ${HistoryAccessInfoButton}:hover & circle,
    ${HistoryAccessInfoButton}:focus-visible & path,
    ${HistoryAccessInfoButton}:focus-visible & circle {
        fill: ${themeObj.verascopeColor.purple400};
        stroke: ${themeObj.verascopeColor.purple400};
    }
`;

export const HistoryAccessInfoText = styled.div`
    color: ${themeObj.color.neutral800};
    font-size: ${0.78 * BODY_FONT_SIZE}px;
    line-height: 1.45em;
    text-align: center;
    max-width: 28rem;
`;

export const HistoryAccessHint = styled.div`
    color: ${themeObj.color.neutral800};
    font-size: ${0.8 * BODY_FONT_SIZE}px;
    line-height: 1.4em;
`;

export const HistoryAccessDateRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${0.45 * BODY_FONT_SIZE}px;
    flex-wrap: wrap;
`;

export const HistoryAccessDateField = styled.div`
    position: relative;
`;

export const HistoryAccessDateTrigger = styled.button<{ disabled?: boolean }>`
    min-width: 9.25rem;
    height: ${1.95 * BODY_FONT_SIZE}px;
    padding: 0 ${2 * BODY_FONT_SIZE}px 0 ${0.75 * BODY_FONT_SIZE}px;
    border: 1px solid ${themeObj.color.neutral400};
    border-radius: ${1.025 * BODY_FONT_SIZE}px;
    background: ${themeObj.color.white};
    color: ${themeObj.color.textBlack};
    font-size: ${0.85 * BODY_FONT_SIZE}px;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    text-align: left;
    white-space: nowrap;
    position: relative;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

    &::after {
        content: '';
        position: absolute;
        top: 50%;
        right: ${0.8 * BODY_FONT_SIZE}px;
        width: ${0.85 * BODY_FONT_SIZE}px;
        height: ${0.55 * BODY_FONT_SIZE}px;
        transform: translateY(-50%);
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='9' viewBox='0 0 14 9' fill='none'%3E%3Cpath d='M1.25 1.5L7 7.25L12.75 1.5' stroke='%23343A40' stroke-width='1.75' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-size: contain;
        background-position: center;
        pointer-events: none;
    }

    &:focus {
        outline: none;
        border-color: ${themeObj.verascopeColor.purple400};
        box-shadow: 0 0 0 2px ${themeObj.color.neutral200};
    }
`;

export const HistoryAccessDatePopover = styled.div`
    position: absolute;
    bottom: calc(100% + ${0.4 * BODY_FONT_SIZE}px);
    left: 0;
    z-index: 10;
    width: 16rem;
    padding: ${0.7 * BODY_FONT_SIZE}px;
    border: 1px solid ${themeObj.color.neutral300};
    border-radius: ${1.025 * BODY_FONT_SIZE}px;
    background: ${themeObj.color.cream500};
    box-shadow: ${themeObj.color.boxShadow300};
`;

export const HistoryAccessCalendarHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${0.45 * BODY_FONT_SIZE}px;
    margin-bottom: ${0.55 * BODY_FONT_SIZE}px;
    background: ${themeObj.color.cream500};
`;

export const HistoryAccessCalendarTitle = styled.div`
    color: ${themeObj.color.textBlack};
    font-size: ${0.85 * BODY_FONT_SIZE}px;
    font-weight: 700;
`;

export const HistoryAccessCalendarNavButton = styled.button`
    width: ${1.7 * BODY_FONT_SIZE}px;
    height: ${1.7 * BODY_FONT_SIZE}px;
    border: 1px solid ${themeObj.color.neutral300};
    border-radius: ${999 * BODY_FONT_SIZE}px;
    background: ${themeObj.color.white};
    color: ${themeObj.color.textBlack};
    font-size: ${0.9 * BODY_FONT_SIZE}px;
    font-weight: 700;
    cursor: pointer;

    &:hover {
        background: ${themeObj.color.cream400};
    }

    &:active {
        background: ${themeObj.color.neutral200};
    }
`;

export const HistoryAccessCalendarWeekdays = styled.div`
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: ${0.2 * BODY_FONT_SIZE}px;
    margin-bottom: ${0.25 * BODY_FONT_SIZE}px;
`;

export const HistoryAccessCalendarWeekday = styled.div`
    color: ${themeObj.color.neutral800};
    font-size: ${0.65 * BODY_FONT_SIZE}px;
    font-weight: 700;
    text-align: center;
`;

export const HistoryAccessCalendarGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: ${0.2 * BODY_FONT_SIZE}px;
    background: ${themeObj.color.cream500};
`;

export const HistoryAccessCalendarDay = styled.button<{
    $selected?: boolean;
    $muted?: boolean;
    $today?: boolean;
}>`
    width: 100%;
    height: ${1.8 * BODY_FONT_SIZE}px;
    border: 1px solid ${({ $selected, $today }) => {
        if ($selected) return themeObj.verascopeColor.purple400;
        if ($today) return themeObj.verascopeColor.purple300;
        return 'transparent';
    }};
    border-width: ${({ $selected, $today }) => (($selected || $today) ? '2px' : '1px')};
    border-radius: ${0.8 * BODY_FONT_SIZE}px;
    background: ${({ $selected, $today }) => {
        if ($selected) return themeObj.verascopeColor.purple400;
        if ($today) return themeObj.color.cream400;
        return themeObj.color.white;
    }};
    color: ${({ $selected, $muted, $today }) => {
        if ($selected) return themeObj.color.white;
        if ($today) return themeObj.verascopeColor.purple300;
        if ($muted) return themeObj.color.neutral700;
        return themeObj.color.textBlack;
    }};
    font-size: ${0.75 * BODY_FONT_SIZE}px;
    font-weight: ${({ $selected, $today }) => (($selected || $today) ? 700 : 600)};
    cursor: pointer;

    &:hover {
        background: ${({ $selected }) => ($selected ? themeObj.verascopeColor.purple400 : themeObj.color.cream400)};
    }

    &:active {
        background: ${({ $selected }) => ($selected ? themeObj.verascopeColor.purple300 : themeObj.color.neutral200)};
    }
`;

export const HistoryAccessCalendarFooter = styled.div`
    display: flex;
    justify-content: flex-start;
    margin-top: ${0.55 * BODY_FONT_SIZE}px;
    background: ${themeObj.color.cream500};
`;

export const HistoryAccessCalendarActionButton = styled.button`
    border: none;
    border-radius: ${999 * BODY_FONT_SIZE}px;
    background: ${themeObj.color.cream400};
    color: ${themeObj.color.textBlack};
    font-size: ${0.75 * BODY_FONT_SIZE}px;
    font-weight: 600;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    padding: ${0.35 * BODY_FONT_SIZE}px ${0.7 * BODY_FONT_SIZE}px;
    cursor: pointer;

    &:hover {
        background: ${themeObj.color.cream300};
    }
`;

export const HistoryAccessActions = styled.div`
    display: flex;
    align-items: center;
    gap: ${0.4 * BODY_FONT_SIZE}px;
    flex: 0 0 auto;
    white-space: nowrap;
    justify-content: center;
    flex-wrap: wrap;
    width: 100%;
`;

export const HistoryPreviewContainer = styled.div`
    position: relative;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    background: ${themeObj.color.white};
    padding: ${BODY_FONT_SIZE}px ${1.25 * BODY_FONT_SIZE}px ${1.5 * BODY_FONT_SIZE}px;
`;

export const HistoryPreviewCutoffMarker = styled.div<{ top: number }>`
    position: absolute;
    top: ${({ top }) => top}px;
    left: ${1.25 * BODY_FONT_SIZE}px;
    right: ${1.25 * BODY_FONT_SIZE}px;
    display: flex;
    align-items: center;
    gap: ${0.5 * BODY_FONT_SIZE}px;
    pointer-events: none;
    z-index: 3;
`;

export const HistoryPreviewCutoffLine = styled.div`
    flex: 1;
    height: 1px;
    background: ${themeObj.verascopeColor.purple400};
`;

export const HistoryPreviewCutoffLabel = styled.div`
    padding: ${0.2 * BODY_FONT_SIZE}px ${0.55 * BODY_FONT_SIZE}px;
    border-radius: ${999 * BODY_FONT_SIZE}px;
    background: ${themeObj.verascopeColor.purple400};
    color: ${themeObj.color.white};
    font-size: ${0.75 * BODY_FONT_SIZE}px;
    font-weight: 700;
    letter-spacing: 0.02em;
    white-space: nowrap;
`;

export const HistoryLoadingState = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100%;
    background: ${themeObj.color.white};
`;

export const HistoryEmptyState = styled.div`
    padding: ${1.25 * BODY_FONT_SIZE}px;
    background: ${themeObj.color.cream500};
    color: ${themeObj.color.neutral800};
`;

export const HistoryErrorState = styled.div`
    padding: ${1.25 * BODY_FONT_SIZE}px;
    background: ${themeObj.color.cream500};
    color: ${themeObj.color.error};
    line-height: 1.5em;
`;
